import { STARTING_GOLD, STARTING_LIVES } from "../config";
import { computeMetaModifiers, MetaModifiers } from "./SkillTree";

type Listener = (s: { gold: number; lives: number }) => void;

/** Selling should fix mistakes without making constant tower swapping free. */
export const BASE_SELL_REFUND_PCT = 0.70;

export class Economy {
  private _gold: number;
  private _lives: number;
  /** The map's castleHp: healing restores up to this, never past it. */
  private readonly _maxLives: number;
  private listeners: Listener[] = [];
  private mods: MetaModifiers;

  constructor(startGold: number = STARTING_GOLD, startLives: number = STARTING_LIVES,
              mods: MetaModifiers = computeMetaModifiers()) {
    this.mods = mods;
    this._gold = startGold + this.mods.startGoldBonus;
    this._lives = startLives;
    this._maxLives = startLives;
  }

  get gold(): number { return this._gold; }
  get lives(): number { return this._lives; }
  get maxLives(): number { return this._maxLives; }
  get isDead(): boolean { return this._lives <= 0; }
  get modifiers(): MetaModifiers { return this.mods; }

  on(fn: Listener): () => void {
    this.listeners.push(fn);
    fn(this.snapshot());
    return () => {
      this.listeners = this.listeners.filter(l => l !== fn);
    };
  }
  private emit(): void { const s = this.snapshot(); for (const l of this.listeners) l(s); }
  private snapshot() { return { gold: this._gold, lives: this._lives }; }

  canAfford(amount: number): boolean { return this._gold >= amount; }

  spend(amount: number): boolean {
    if (!this.canAfford(amount)) return false;
    this._gold -= amount;
    this.emit();
    return true;
  }

  reward(amount: number): void {
    this._gold += amount + this.mods.killBounty;
    this.emit();
  }

  /** Called by WaveManager when a wave is cleared. Includes meta wave-bonus + interest. */
  applyWaveBonus(waveNumber: number): number {
    const base = 10 + waveNumber * 2;
    const interestPct = Math.min(0.05, this.mods.interestPctPer100 * Math.floor(this._gold / 100));
    const interest = Math.floor(this._gold * interestPct);
    const total = base + this.mods.waveBonusGold + interest;
    this._gold += total;
    this.emit();
    return total;
  }

  get sellRefundPct(): number {
    return Math.min(1, BASE_SELL_REFUND_PCT + this.mods.sellRefundPct);
  }

  quoteSellRefund(totalCost: number): number {
    return Math.floor(Math.max(0, totalCost) * this.sellRefundPct);
  }

  /** Refund the quoted share of the tower's exact build + upgrade spend. */
  sellRefund(totalCost: number): number {
    const amt = this.quoteSellRefund(totalCost);
    this._gold += amt;
    this.emit();
    return amt;
  }

  loseLives(amount: number): void {
    this._lives = Math.max(0, this._lives - amount);
    this.emit();
  }

  /** Heal the keep (e.g. via Commander ability), never past its starting HP. */
  healLives(amount: number): void {
    this._lives = Math.min(this._maxLives, this._lives + amount);
    this.emit();
  }

  /** Gain gold without applying killBounty (e.g. ability gold grant). */
  grantGold(amount: number): void {
    this._gold += amount;
    this.emit();
  }
}
